import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const glowButtonVariants = cva(
  "relative inline-flex items-center justify-center whitespace-nowrap flex-nowrap rounded-full text-sm font-semibold transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 overflow-hidden",
  {
    variants: {
      variant: {
        default: "bg-gradient-primary text-white hover:shadow-glow-lg",
        secondary: "bg-gradient-secondary text-white hover:shadow-glow-lg",
        aurora: "bg-gradient-aurora text-white hover:shadow-glow-lg animated-gradient",
        outline: "border-2 border-primary text-primary hover:bg-primary hover:text-white hover:shadow-glow-md",
        ghost: "text-primary bg-white/60 hover:bg-primary hover:text-white border border-primary/20 hover:border-primary hover:shadow-glow-sm transition-all duration-300",
        "outline-soft": "text-slate-600 bg-white/80 hover:bg-slate-100 hover:text-slate-700 border border-slate-300/60 hover:border-slate-400/80 hover:shadow-sm transition-all duration-300",
      },
      size: {
        default: "h-11 px-8 py-2",
        sm: "h-9 px-6",
        lg: "h-14 px-12 text-base",
        xl: "h-16 px-16 text-lg",
        icon: "h-10 w-10",
      },
      pulse: {
        true: "animate-pulse-glow",
        false: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      pulse: false,
    },
  }
)

export interface GlowButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof glowButtonVariants> {
  asChild?: boolean
  shimmer?: boolean
}

const GlowButton = React.forwardRef<HTMLButtonElement, GlowButtonProps>(
  ({ className, variant, size, pulse, shimmer = false, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    
    // When asChild is true, only render the direct children
    if (asChild) {
      return (
        <Comp
          className={cn(glowButtonVariants({ variant, size, pulse, className }))}
          ref={ref}
          {...props}
        >
          {children}
        </Comp>
      )
    }
    
    // Normal rendering with all decorative elements
    return (
      <Comp
        className={cn(glowButtonVariants({ variant, size, pulse, className }))}
        ref={ref}
        {...props}
      >
        {shimmer && (
          <div className="absolute inset-0 -top-2 -bottom-2 opacity-0 transition-opacity duration-300 hover:opacity-100">
            <div className="absolute inset-0 shimmer" />
          </div>
        )}
        <span className="relative z-10 flex items-center gap-2 whitespace-nowrap">{children}</span>
        <div className="absolute inset-0 rounded-full bg-white opacity-0 transition-opacity duration-300 hover:opacity-10" />
      </Comp>
    )
  }
)
GlowButton.displayName = "GlowButton"

export { GlowButton }
